import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Building, Building2, ClipboardList, FileSignature, Gauge, LayoutGrid, LogOut, Mail, Menu, Settings, Users, X } from 'lucide-react';
import type { Roli } from '@albmedtech/shared';
import { useAuth } from '@/auth/AuthProvider';
import { Ngarkim } from './ui';

interface Lidhje {
  te: string;
  etiketa: string;
  ikona: ReactNode;
  rolet: Roli[];
}

const LIDHJET: Lidhje[] = [
  { te: '/paneli', etiketa: 'Paneli', ikona: <Gauge className="h-4 w-4" />, rolet: ['superadmin', 'staf', 'financa', 'klient'] },
  { te: '/institucione', etiketa: 'Institucionet', ikona: <Building2 className="h-4 w-4" />, rolet: ['superadmin', 'staf', 'financa'] },
  { te: '/institucioni-im', etiketa: 'Institucioni im', ikona: <Building className="h-4 w-4" />, rolet: ['klient'] },
  { te: '/pajisje', etiketa: 'Pajisjet', ikona: <ClipboardList className="h-4 w-4" />, rolet: ['superadmin', 'staf', 'financa', 'klient'] },
  { te: '/sherbimet', etiketa: 'Shërbimet', ikona: <LayoutGrid className="h-4 w-4" />, rolet: ['superadmin', 'staf', 'financa', 'klient'] },
  { te: '/aplikime', etiketa: 'Aplikimet', ikona: <FileSignature className="h-4 w-4" />, rolet: ['superadmin', 'staf', 'klient'] },
  { te: '/mesazhet', etiketa: 'Mesazhet', ikona: <Mail className="h-4 w-4" />, rolet: ['superadmin', 'staf', 'financa', 'klient'] },
  { te: '/perdoruesit', etiketa: 'Përdoruesit', ikona: <Users className="h-4 w-4" />, rolet: ['superadmin'] },
  { te: '/konfigurimi', etiketa: 'Konfigurimi', ikona: <Settings className="h-4 w-4" />, rolet: ['superadmin'] },
];

const EMRAT_E_ROLEVE: Record<Roli, string> = {
  superadmin: 'Administrator',
  staf: 'Staf teknik',
  financa: 'Financa',
  klient: 'Klient',
};

/** Rrugë e mbrojtur: kërkon identifikim dhe një nga rolet e dhëna. */
export function RrugeMbrojtur({ rolet }: { rolet: Roli[] }) {
  const { ngarkohet, perdoruesi, roli } = useAuth();
  const vendi = useLocation();
  if (ngarkohet) return <Ngarkim />;
  if (!perdoruesi) return <Navigate to="/hyrje" state={{ nga: vendi.pathname }} replace />;
  if (!roli) return <Navigate to="/ne-pritje" replace />;
  if (!rolet.includes(roli)) return <Navigate to="/paneli" replace />;
  return <Outlet />;
}

export function Layout() {
  const { roli, perdoruesi, profili, dil, institucioniId } = useAuth();
  const [hapur, setHapur] = useState(false);
  // Klientët individualë (pa institucion) nuk kanë faqen "Institucioni im".
  const lidhjet = LIDHJET.filter((l) => roli && l.rolet.includes(roli) && !(l.te === '/institucioni-im' && !institucioniId));

  const nav = (
    <nav className="flex flex-col gap-0.5 px-3">
      {lidhjet.map((l) => (
        <NavLink
          key={l.te}
          to={l.te}
          onClick={() => setHapur(false)}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium',
              isActive ? 'bg-marka-50 text-marka-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          }
        >
          {l.ikona}
          {l.etiketa}
        </NavLink>
      ))}
    </nav>
  );

  const marka = (
    <div className="flex items-center gap-2 px-6 py-5">
      <img src="/logo.png" alt="ALBMEDTECH" className="h-10 w-10 shrink-0" />
      <div className="leading-tight">
        <p className="text-sm font-semibold tracking-wide text-slate-900">ALBMEDTECH</p>
        <p className="text-[11px] text-slate-500">Portali i menaxhimit</p>
      </div>
    </div>
  );

  const perdoruesiBlok = (
    <div className="border-t border-slate-200 px-4 py-3">
      <p className="truncate text-sm font-medium text-slate-800">{profili?.emri ?? perdoruesi?.displayName ?? perdoruesi?.email}</p>
      <p className="truncate text-xs text-slate-500">{roli ? EMRAT_E_ROLEVE[roli] : ''}</p>
      <button onClick={() => void dil()} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900">
        <LogOut className="h-3.5 w-3.5" /> Dil
      </button>
    </div>
  );

  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        {marka}
        <div className="flex-1">{nav}</div>
        {perdoruesiBlok}
      </aside>

      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="ALBMEDTECH" className="h-8 w-8" />
          <span className="text-sm font-semibold">ALBMEDTECH</span>
        </div>
        <button onClick={() => setHapur(true)} aria-label="Hap menunë" className="rounded p-2 hover:bg-slate-100">
          <Menu className="h-5 w-5" />
        </button>
      </div>
      {hapur && (
        <div className="fixed inset-0 z-30 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setHapur(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between pr-3">
              {marka}
              <button onClick={() => setHapur(false)} aria-label="Mbyll" className="rounded p-2 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1">{nav}</div>
            {perdoruesiBlok}
          </aside>
        </div>
      )}

      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
}
